import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Slack, Building2, Plug, CheckCircle2, XCircle, ExternalLink, Trash2, Sparkles, ShieldCheck, AlertTriangle, ScrollText, Check, X as XIcon, Loader2, Save, Globe, Lock,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { CATEGORY_META } from "@/components/ConsentGate";

function AgreementBumpPanel({ currentVersion, onBumped }) {
  const [newVersion, setNewVersion] = useState("");
  const [reason, setReason] = useState("");
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);
  const suggest = () => {
    // EA-1.0 → EA-1.1, EA-2.0 → EA-2.1, EA-1.10 → EA-1.11 …
    const m = /^([A-Za-z]+-)?(\d+)(?:\.(\d+))?$/.exec(currentVersion || "");
    if (!m) return setNewVersion("EA-1.1");
    const prefix = m[1] || "EA-";
    const major = parseInt(m[2]);
    const minor = m[3] !== undefined ? parseInt(m[3]) + 1 : 1;
    setNewVersion(`${prefix}${major}.${minor}`);
  };
  const submit = async () => {
    if (!newVersion.trim()) return toast.error("Enter a version identifier");
    if (!confirm(`Bump agreement to ${newVersion}? Every employee will be asked to re-accept on their next visit.`)) return;
    setBusy(true);
    try {
      const r = await api.post("/agreement/bump-version", {
        new_version: newVersion.trim(), reason: reason.trim() || null, notify_email: notify,
      });
      toast.success(`Bumped to ${r.data.new_version} · ${r.data.email_sent} emails queued`);
      setNewVersion(""); setReason("");
      onBumped && onBumped();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-8 border border-amber-500/30 bg-amber-500/5 rounded-lg p-4" data-testid="agreement-bump-panel">
      <p className="text-sm font-medium mb-1">Bump agreement version</p>
      <p className="text-xs text-muted-foreground mb-3">
        Current: <span className="font-mono">{currentVersion || "—"}</span>. Bumping forces all members to review and re-accept via the wizard.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <input
          data-testid="agreement-bump-version-input"
          value={newVersion}
          onChange={(e) => setNewVersion(e.target.value)}
          placeholder="e.g. EA-1.1"
          className="px-3 py-2 bg-background border border-border/60 rounded text-sm font-mono w-32"
        />
        <Button size="sm" variant="ghost" onClick={suggest} data-testid="agreement-bump-suggest">Suggest</Button>
        <input
          data-testid="agreement-bump-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason (optional — added to email)"
          className="flex-1 min-w-[240px] px-3 py-2 bg-background border border-border/60 rounded text-sm"
        />
        <label className="text-xs text-muted-foreground flex items-center gap-1.5">
          <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)}
                 data-testid="agreement-bump-notify" />
          email everyone
        </label>
        <Button size="sm" onClick={submit} disabled={busy}
                data-testid="agreement-bump-submit"
                className="rounded-full">
          {busy && <Loader2Icon />}
          Bump &amp; force re-accept
        </Button>
      </div>
    </div>
  );
}

function ComplianceSection() {
  const [status, setStatus] = useState(null);
  const [pending, setPending] = useState([]);
  const [agree, setAgree] = useState(null);
  const [agFilter, setAgFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try {
      const [s, p, a] = await Promise.all([
        api.get("/compliance/status"),
        api.get("/compliance/pending-deletions"),
        api.get("/agreement/record"),
      ]);
      setStatus(s.data); setPending(p.data); setAgree(a.data);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const toggle = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/compliance/dpdp", { enabled: !status.dpdp_mode });
      toast.success(`DPDP mode ${data.dpdp_mode ? "enabled" : "disabled"}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const approve = async (pid) => { try { await api.post(`/compliance/pending-deletions/${pid}/approve`); toast.success("Deletion approved"); load(); } catch (err) { toast.error(err.response?.data?.detail || "Failed"); } };
  const reject = async (pid) => { try { await api.post(`/compliance/pending-deletions/${pid}/reject`); toast.success("Deletion rejected"); load(); } catch (err) { toast.error(err.response?.data?.detail || "Failed"); } };
  const resendAgreement = async (uid) => {
    try {
      const { data } = await api.post(`/agreement/resend-email/${uid}`);
      toast.success(data.email_sent ? "Email re-sent" : "Email delivery pending — check Resend config");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const exportCsv = async () => {
    try {
      const res = await api.get("/agreement/record", { params: { format: "csv" }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `employee_agreements.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  if (!status) return null;
  const agRows = (agree?.rows || []).filter((r) => agFilter === "all" ? true : agFilter === "accepted" ? r.accepted : !r.accepted);
  const pct = agree && agree.total_members ? Math.round((agree.accepted / agree.total_members) * 100) : 0;

  return (
    <section className="mb-10" data-testid="compliance-section">
      <h2 className="font-medium mb-1 flex items-center gap-2"><ScrollText className="w-4 h-4" /> Compliance &amp; DPDP</h2>
      <p className="text-xs text-muted-foreground mb-4">
        Turn on DPDP mode to require reasons on record reads and two-admin approval on deletes. 365-day audit-log retention.
      </p>
      <div className="card-flat p-4 flex items-center justify-between gap-4 mb-5">
        <div>
          <p className="text-sm font-medium">DPDP mode {status.dpdp_mode ? "on" : "off"}</p>
          <p className="text-xs text-muted-foreground">
            {status.dpdp_mode
              ? "Reads require a reason · deletes need a 2nd admin · non-consented captures return 451"
              : "Standard mode — captures skip silently when consent is missing"}
          </p>
        </div>
        <Button onClick={toggle} disabled={busy} variant={status.dpdp_mode ? "outline" : "default"}
          className="rounded-full" data-testid="compliance-dpdp-toggle">
          {busy && <Loader2Icon />}
          {status.dpdp_mode ? "Disable" : "Enable"} DPDP
        </Button>
      </div>

      <p className="eyebrow mb-3">Pending deletions ({pending.length})</p>
      {pending.length === 0 ? (
        <p className="text-xs text-muted-foreground italic" data-testid="compliance-pending-empty">No pending deletion requests.</p>
      ) : (
        <div className="card-flat divide-y divide-border/60 mb-8" data-testid="compliance-pending-list">
          {pending.map((p) => (
            <div key={p.id} className="p-3.5 flex flex-wrap items-center gap-3 text-sm" data-testid={`compliance-pending-row-${p.id}`}>
              <span className="text-[10px] font-mono uppercase tracking-wider border rounded-full px-2 py-0.5 text-red-400 border-red-500/30 bg-red-500/5">delete</span>
              <span className="text-xs font-mono text-muted-foreground">{p.resource_type}</span>
              <span className="text-[11px] font-mono text-muted-foreground">#{p.resource_id.slice(0, 8)}</span>
              <span className="flex-1 min-w-0 truncate">
                by <span className="text-foreground">{p.requested_by_user?.name || p.requested_by_user?.email}</span>
                {p.reason && <span className="text-muted-foreground"> · {p.reason}</span>}
              </span>
              <div className="flex gap-1.5 shrink-0">
                <Button size="sm" className="rounded-full h-8 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => approve(p.id)} data-testid={`compliance-approve-${p.id}`}><Check className="w-3.5 h-3.5 mr-1" /> Approve</Button>
                <Button size="sm" variant="outline" className="rounded-full h-8 text-xs" onClick={() => reject(p.id)} data-testid={`compliance-reject-${p.id}`}><XIcon className="w-3.5 h-3.5 mr-1" /> Reject</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Employee agreements */}
      <div className="mt-10" data-testid="compliance-agreements">
        <div className="flex items-center justify-between mb-3 gap-3">
          <div>
            <p className="eyebrow mb-1">Employee agreements ({agree?.version || "—"})</p>
            <p className="text-xs text-muted-foreground">
              {agree?.accepted || 0} of {agree?.total_members || 0} employees have accepted · {pct}%
            </p>
          </div>
          <Button size="sm" variant="outline" className="rounded-full" onClick={exportCsv} data-testid="compliance-agreements-csv">Export CSV</Button>
        </div>
        <div className="flex gap-1 mb-3">
          {[{ k: "all", l: "All" }, { k: "accepted", l: "Accepted" }, { k: "pending", l: "Pending" }].map((o) => (
            <button key={o.k} onClick={() => setAgFilter(o.k)}
              className={`px-3 py-1 text-xs rounded-full border ${agFilter === o.k ? "bg-foreground text-background border-foreground" : "border-border/60 text-muted-foreground hover:text-foreground"}`}
              data-testid={`compliance-agreements-filter-${o.k}`}>{o.l}</button>
          ))}
        </div>
        {agRows.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">No matching employees.</p>
        ) : (
          <div className="card-flat divide-y divide-border/60" data-testid="compliance-agreements-list">
            {agRows.map((r) => (
              <div key={r.user_id}
                className={`p-3.5 flex flex-wrap items-center gap-3 text-sm ${!r.accepted ? "bg-amber-500/5" : ""}`}
                data-testid={`compliance-agreements-row-${r.user_id}`}>
                <div className="flex-1 min-w-[180px]">
                  <p className="font-medium">{r.name || r.email}</p>
                  <p className="text-[11px] text-muted-foreground font-mono">{r.email}</p>
                </div>
                {r.accepted ? (
                  <>
                    <span className="text-[11px] text-muted-foreground">{new Date(r.accepted_at).toLocaleString()}</span>
                    <span className="text-[10px] font-mono text-muted-foreground">{r.ip_address || "—"}</span>
                    <span className={`text-[10px] font-mono uppercase tracking-wider rounded-full px-2 py-0.5 ${r.email_sent ? "text-emerald-500 border border-emerald-500/30 bg-emerald-500/5" : "text-muted-foreground border border-border"}`}>
                      {r.email_sent ? "email sent" : "email pending"}
                    </span>
                    <Button size="sm" variant="outline" className="rounded-full h-7 text-xs" onClick={() => resendAgreement(r.user_id)} data-testid={`compliance-agreements-resend-${r.user_id}`}>Resend email</Button>
                  </>
                ) : (
                  <span className="text-[10px] font-mono uppercase tracking-wider rounded-full px-2 py-0.5 text-amber-500 border border-amber-500/30 bg-amber-500/5">pending</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      <AgreementBumpPanel currentVersion={agree?.version} onBumped={load} />
    </section>
  );
}
function Loader2Icon() { return <span className="mr-1.5 inline-block w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />; }

function PrivacySection() {
  const [status, setStatus] = useState(null);
  const [toggles, setToggles] = useState({});
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/consent/status");
      setStatus(data);
      setToggles({ ...Object.fromEntries(CATEGORY_META.map((c) => [c.key, false])), ...(data.capture_categories || {}) });
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/consent", { capture_categories: toggles });
      toast.success("Capture preferences updated");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const eraseAll = async () => {
    const n = status?.entries_captured ?? 0;
    if (!window.confirm(`This will permanently delete ${n} memory entries and your handover pack. This cannot be undone.`)) return;
    try {
      const { data } = await api.delete("/consent");
      toast.success(`Deleted ${data.deleted_entries} entries — capture is now off`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  if (!status) return null;
  return (
    <section className="mb-10" data-testid="privacy-capture-section">
      <h2 className="font-medium mb-1 flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Privacy & Capture</h2>
      <p className="text-xs text-muted-foreground mb-4">
        Policy {status.policy_version} · {status.entries_captured} entries captured so far
        {status.consented_at && ` · consented ${new Date(status.consented_at).toLocaleDateString()}`}
      </p>
      <div className="card-flat divide-y divide-border/60">
        {CATEGORY_META.map((c) => (
          <div key={c.key} className="px-4 py-3 flex items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">{c.label}</p>
              <p className="text-[11px] text-muted-foreground">{c.cap}</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={!!toggles[c.key]}
              onClick={() => setToggles({ ...toggles, [c.key]: !toggles[c.key] })}
              className={`w-10 h-6 rounded-full transition-colors relative shrink-0 ${toggles[c.key] ? "bg-emerald-500" : "bg-secondary border border-border/60"}`}
              data-testid={`settings-toggle-${c.key}`}
            >
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${toggles[c.key] ? "left-[18px]" : "left-0.5"}`} />
            </button>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mt-4">
        <Button onClick={save} disabled={busy} className="rounded-full" data-testid="privacy-save-btn">
          {busy ? "Saving…" : "Save preferences"}
        </Button>
        <Button variant="outline" onClick={eraseAll} className="rounded-full text-destructive border-destructive/50 hover:bg-destructive hover:text-destructive-foreground" data-testid="privacy-erase-btn">
          <AlertTriangle className="w-3.5 h-3.5 mr-1.5" /> Delete all my captured data
        </Button>
      </div>
    </section>
  );
}

const PROVIDERS = [
  {
    id: "slack",
    name: "Slack",
    description: "Sync channel messages, run slash-commands, and let the assistant reference conversations.",
    icon: Slack,
  },
  {
    id: "hubspot",
    name: "HubSpot",
    description: "Pull deals, contacts, and notes so answers stay grounded in your CRM.",
    icon: Building2,
  },
  {
    id: "gmail",
    name: "Gmail",
    description: "Search past threads, summarise emails, and reference decisions made over mail.",
    icon: Plug,
  },
];

function GCalPanel() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try { const r = await api.get("/gcal/status"); setStatus(r.data); }
    catch { setStatus({ configured: false, connected: false }); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("gcal") === "connected") toast.success("Google Calendar connected.");
    if (p.get("gcal") === "error") toast.error("Google Calendar connection failed: " + (p.get("reason") || "unknown"));
  }, []);
  const connect = async () => {
    setBusy(true);
    try {
      const r = await api.get("/gcal/authorize");
      window.location.href = r.data.authorization_url;
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); setBusy(false); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect Google Calendar?")) return;
    try { await api.delete("/gcal"); toast.success("Disconnected"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const syncNow = async () => {
    setBusy(true);
    try {
      const r = await api.post("/gcal/sync");
      toast.success(`Synced ${r.data.synced} calendar events for ${r.data.date}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sync failed");
    } finally { setBusy(false); }
  };
  if (!status) return null;
  return (
    <div className="card-flat p-5" data-testid="gcal-panel">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-lg bg-secondary/60 flex items-center justify-center text-xl">📅</div>
        <div className="flex-1">
          <p className="font-medium">Google Calendar</p>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
            {status.connected
              ? <><CheckCircle2 className="w-3 h-3 text-emerald-500" /> Connected as {status.email}</>
              : status.configured
              ? <><XCircle className="w-3 h-3 text-muted-foreground" /> Not connected</>
              : <><AlertTriangle className="w-3 h-3 text-amber-500" /> Not configured on server</>}
          </p>
        </div>
      </div>
      <p className="text-sm text-muted-foreground leading-relaxed mb-4">
        Pull today's meetings + focus blocks into your Work Log automatically. We only read event titles, times, and attendee counts — never event descriptions or attachments.
      </p>
      {!status.configured ? (
        <p className="text-xs text-amber-500" data-testid="gcal-config-warning">Admin needs to set GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET in backend .env.</p>
      ) : status.connected ? (
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={syncNow} disabled={busy} className="rounded-full" data-testid="gcal-sync-btn">
            {busy && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
            Sync today
          </Button>
          <Button size="sm" variant="ghost" onClick={disconnect} data-testid="gcal-disconnect-btn"
                  className="rounded-full text-destructive hover:text-destructive">
            <Trash2 className="w-3 h-3 mr-1" /> Disconnect
          </Button>
          {status.last_synced_at && (
            <p className="text-[11px] text-muted-foreground ml-auto" data-testid="gcal-last-sync">
              Last sync: {new Date(status.last_synced_at).toLocaleString()} · {status.last_sync_count} events
            </p>
          )}
        </div>
      ) : (
        <Button onClick={connect} disabled={busy} className="rounded-full" data-testid="gcal-connect-btn">
          {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <ExternalLink className="w-3.5 h-3.5 mr-1.5" />}
          Connect with Google
        </Button>
      )}
    </div>
  );
}


function WhatsAppPanel() {
  const [status, setStatus] = useState(null);
  const [linkPayload, setLinkPayload] = useState(null);
  const [testMsg, setTestMsg] = useState("Ping from Ragnes 👋");
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try { const r = await api.get("/whatsapp/status"); setStatus(r.data); }
    catch { setStatus(null); }
  };
  useEffect(() => { load(); }, []);
  const generateCode = async () => {
    setBusy(true);
    try {
      const r = await api.post("/whatsapp/link-code");
      setLinkPayload(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const unlink = async () => {
    if (!window.confirm("Unlink your WhatsApp number?")) return;
    try { await api.delete("/whatsapp"); toast.success("Unlinked"); setLinkPayload(null); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const sendTest = async () => {
    setBusy(true);
    try {
      const r = await api.post("/whatsapp/send", { body: testMsg });
      if (r.data.sent) toast.success("Sent to your WhatsApp");
      else toast.warning(`Not sent (${r.data.reason || "check config"})`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  if (!status) return null;
  return (
    <div className="card-flat p-5" data-testid="whatsapp-panel">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center text-xl">💬</div>
        <div className="flex-1">
          <p className="font-medium">WhatsApp bot</p>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
            {status.linked
              ? <><CheckCircle2 className="w-3 h-3 text-emerald-500" /> Linked to {status.phone_e164}</>
              : status.outbound_configured
              ? <><XCircle className="w-3 h-3 text-muted-foreground" /> Not linked</>
              : <><AlertTriangle className="w-3 h-3 text-amber-500" /> Stub mode (partial credentials)</>}
          </p>
        </div>
      </div>
      <p className="text-sm text-muted-foreground leading-relaxed mb-4">
        Ping your own Ragnes bot from WhatsApp. Ask "what did I decide about X" and get an answer from your personal memory — no app switch needed.
      </p>

      {status.linked ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <input value={testMsg} onChange={(e) => setTestMsg(e.target.value)}
                   className="flex-1 px-3 py-2 rounded border border-border/60 bg-background text-sm"
                   data-testid="whatsapp-test-input" />
            <Button size="sm" onClick={sendTest} disabled={busy || !testMsg.trim()} data-testid="whatsapp-test-send">
              {busy && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
              Send test
            </Button>
            <Button size="sm" variant="ghost" onClick={unlink} data-testid="whatsapp-unlink"
                    className="text-destructive hover:text-destructive">
              <Trash2 className="w-3 h-3 mr-1" /> Unlink
            </Button>
          </div>
          {status.linked_at && <p className="text-[11px] text-muted-foreground">Linked {new Date(status.linked_at).toLocaleString()}</p>}
        </div>
      ) : linkPayload ? (
        <div className="rounded-lg border border-sky-500/40 bg-sky-500/5 p-4" data-testid="whatsapp-link-payload">
          <p className="text-xs uppercase tracking-wider text-sky-500 mb-2">Next step</p>
          <p className="text-sm leading-relaxed">{linkPayload.instruction}</p>
          <p className="text-[11px] text-muted-foreground mt-2 font-mono">Code: <strong>{linkPayload.code}</strong> · expires {new Date(linkPayload.expires_at).toLocaleTimeString()}</p>
          <div className="flex gap-2 mt-3">
            <Button size="sm" variant="outline" onClick={load} data-testid="whatsapp-refresh-status">Refresh status</Button>
            <Button size="sm" variant="ghost" onClick={() => setLinkPayload(null)}>Cancel</Button>
          </div>
        </div>
      ) : (
        <Button onClick={generateCode} disabled={busy} className="rounded-full" data-testid="whatsapp-generate-code">
          {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
          Generate link code
        </Button>
      )}
      {!status.outbound_configured && (
        <p className="text-[11px] text-amber-500 mt-3">
          Admin: set TWILIO_ACCOUNT_SID and TWILIO_AUTH_TOKEN in backend .env to enable real outbound messages and webhook verification.
        </p>
      )}
    </div>
  );
}


function MeetingTypeRulesPanel() {
  const { workspace, isAdmin, user } = useAuth();
  const [rules, setRules] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [kw, setKw] = useState("");
  const [kind, setKind] = useState("review");
  const [busy, setBusy] = useState(false);
  const [showSave, setShowSave] = useState(false);
  const [tplName, setTplName] = useState("");
  const [tplPublish, setTplPublish] = useState(false);
  const [tplCategory, setTplCategory] = useState("");
  const [tplSuggestion, setTplSuggestion] = useState(null);
  const CATEGORIES = ["engineering", "sales", "design", "people-ops",
                      "customer-success", "marketing", "finance",
                      "operations", "general"];
  const load = async () => {
    if (!workspace?.id) return;
    try {
      const [r, t] = await Promise.all([
        api.get(`/workspaces/${workspace.id}/meeting-rules`),
        api.get("/workspaces/meeting-rules/templates"),
      ]);
      setRules(r.data.rules || []);
      setTemplates(t.data.templates || []);
    } catch { setRules([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [workspace?.id]);
  const add = async () => {
    if (!kw.trim() || kw.trim().length < 2) return toast.error("Keyword must be at least 2 characters");
    setBusy(true);
    try {
      await api.post(`/workspaces/${workspace.id}/meeting-rules`,
                     { keyword: kw.trim(), kind });
      toast.success(`Rule added — "${kw}" → ${kind}`);
      setKw(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const remove = async (keyword) => {
    if (!window.confirm(`Delete rule "${keyword}"?`)) return;
    try {
      await api.delete(`/workspaces/${workspace.id}/meeting-rules/${encodeURIComponent(keyword)}`);
      toast.success("Rule deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const applyTemplate = async (tid) => {
    if (!window.confirm(`Apply the "${tid}" preset? Existing keywords with the same name will be replaced.`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/workspaces/${workspace.id}/meeting-rules/apply-template`,
                               { template_id: tid });
      toast.success(`Loaded — ${r.data.added} rules (${r.data.replaced} replaced)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const saveTemplate = async () => {
    if (!tplName.trim() || tplName.trim().length < 2) return toast.error("Name must be at least 2 characters");
    setBusy(true);
    try {
      await api.post(`/workspaces/${workspace.id}/meeting-rule-templates`,
                     { name: tplName.trim(), publish: tplPublish,
                       category: tplCategory || undefined });
      toast.success(tplPublish ? "Template published — visible to every admin" : "Template saved as private draft");
      setShowSave(false); setTplName(""); setTplPublish(false); setTplCategory(""); setTplSuggestion(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const openSaveDialog = async () => {
    setShowSave(true);
    // Auto-detect category from current rule keywords.
    try {
      const r = await api.get(`/workspaces/${workspace.id}/meeting-rule-templates/suggest-category`);
      setTplSuggestion(r.data);
      if (!tplCategory && r.data.suggested && r.data.suggested !== "general") {
        setTplCategory(r.data.suggested);
      }
    } catch { setTplSuggestion(null); }
  };
  const publishTemplate = async (tid) => {
    try {
      await api.post(`/workspaces/meeting-rule-templates/${tid}/publish`);
      toast.success("Published — every admin can now apply this template");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const unpublishTemplate = async (tid) => {
    try {
      await api.post(`/workspaces/meeting-rule-templates/${tid}/unpublish`);
      toast.success("Made private");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const deleteTemplate = async (tid, name) => {
    if (!window.confirm(`Delete template "${name}"? This can't be undone.`)) return;
    try {
      await api.delete(`/workspaces/meeting-rule-templates/${tid}`);
      toast.success("Template deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const KINDS = ["focus", "one_on_one", "sprint", "review", "client", "interview", "meeting", "ooo"];
  const KIND_COLORS = {
    focus: "bg-emerald-500/15 text-emerald-500 border-emerald-500/40",
    one_on_one: "bg-amber-500/15 text-amber-500 border-amber-500/40",
    sprint: "bg-sky-500/15 text-sky-500 border-sky-500/40",
    review: "bg-fuchsia-500/15 text-fuchsia-500 border-fuchsia-500/40",
    client: "bg-rose-500/15 text-rose-500 border-rose-500/40",
    interview: "bg-violet-500/15 text-violet-500 border-violet-500/40",
    meeting: "bg-zinc-500/15 text-zinc-500 border-zinc-500/40",
    ooo: "bg-slate-500/15 text-slate-500 border-slate-500/40",
  };
  const builtinTemplates = templates.filter((t) => t.kind !== "custom");
  const customTemplates = templates.filter((t) => t.kind === "custom");
  return (
    <section className="mb-10" data-testid="meeting-rules-section">
      <h2 className="font-medium mb-1">Meeting type rules</h2>
      <p className="text-xs text-muted-foreground mb-4 max-w-2xl">
        Teach Ragnes your team's meeting vocabulary. When a Google Calendar event's title contains any of these keywords, it's classified as the chosen category in Work Log.
      </p>
      <div className="card-flat p-5">
        {isAdmin && builtinTemplates.length > 0 && (
          <div className="mb-4 pb-4 border-b border-border/40">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">One-click presets</p>
            <div className="flex flex-wrap gap-2" data-testid="meeting-rule-templates">
              {builtinTemplates.map((t) => (
                <Button key={t.id} size="sm" variant="outline"
                        onClick={() => applyTemplate(t.id)} disabled={busy}
                        className="rounded-full"
                        data-testid={`meeting-rule-template-${t.id}`}>
                  <Sparkles className="w-3 h-3 mr-1.5" />
                  Load {t.name} <span className="text-muted-foreground ml-1">({t.count})</span>
                </Button>
              ))}
            </div>
          </div>
        )}
        {isAdmin && (
          <div className="mb-4 pb-4 border-b border-border/40" data-testid="custom-templates-block">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Custom template bundles</p>
              <Button size="sm" variant="outline" onClick={openSaveDialog}
                      disabled={busy || rules.length === 0}
                      className="rounded-full"
                      data-testid="save-as-template-btn">
                <Save className="w-3 h-3 mr-1.5" />
                Save current rules as template
              </Button>
            </div>
            {customTemplates.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">No custom templates yet — save your rules bundle so you can reuse it in other workspaces you admin, or publish it for every admin.</p>
            ) : (
              <div className="flex flex-wrap gap-2" data-testid="custom-template-list">
                {customTemplates.map((t) => (
                  <div key={t.id}
                       data-testid={`custom-template-${t.id}`}
                       className="inline-flex items-center gap-1.5 text-xs pl-3 pr-1.5 py-1.5 rounded-full border border-border/60 bg-secondary/20">
                    <span className="font-medium">{t.name}</span>
                    <span className="text-muted-foreground">({t.count})</span>
                    {t.is_published ? (
                      <span className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-500 uppercase tracking-wider">
                        <Globe className="w-2.5 h-2.5" /> published
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-full bg-zinc-500/15 text-zinc-500 uppercase tracking-wider">
                        <Lock className="w-2.5 h-2.5" /> draft
                      </span>
                    )}
                    {!t.is_mine && t.owner && (
                      <span className="text-[10px] text-muted-foreground">by {t.owner.name || t.owner.email}</span>
                    )}
                    <button onClick={() => applyTemplate(t.id)}
                            disabled={busy}
                            className="ml-1 text-[10px] uppercase tracking-wider text-sky-500 hover:underline px-1.5 py-1"
                            data-testid={`custom-template-apply-${t.id}`}>
                      Apply
                    </button>
                    {t.is_mine && !t.is_published && (
                      <button onClick={() => publishTemplate(t.id)}
                              className="text-[10px] uppercase tracking-wider text-emerald-500 hover:underline px-1.5 py-1"
                              data-testid={`custom-template-publish-${t.id}`}>
                        Publish
                      </button>
                    )}
                    {t.is_mine && t.is_published && (
                      <button onClick={() => unpublishTemplate(t.id)}
                              className="text-[10px] uppercase tracking-wider text-amber-500 hover:underline px-1.5 py-1"
                              data-testid={`custom-template-unpublish-${t.id}`}>
                        Unpublish
                      </button>
                    )}
                    {t.is_mine && (
                      <button onClick={() => deleteTemplate(t.id, t.name)}
                              className="text-muted-foreground hover:text-destructive p-1"
                              data-testid={`custom-template-delete-${t.id}`}>
                        <XIcon className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {isAdmin && (
          <div className="flex flex-wrap items-end gap-2 mb-4">
            <div className="flex-1 min-w-[180px]">
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Keyword</label>
              <input
                data-testid="meeting-rule-keyword"
                value={kw}
                onChange={(e) => setKw(e.target.value)}
                placeholder="e.g. board sync"
                className="w-full mt-1 px-3 py-2 rounded border border-border/60 bg-background text-sm"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Category</label>
              <select
                data-testid="meeting-rule-kind"
                value={kind}
                onChange={(e) => setKind(e.target.value)}
                className="mt-1 px-3 py-2 rounded border border-border/60 bg-background text-sm"
              >
                {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
            </div>
            <Button size="sm" onClick={add} disabled={busy}
                    className="rounded-full"
                    data-testid="meeting-rule-add">
              {busy && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
              Add rule
            </Button>
          </div>
        )}
        {rules.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">No custom rules yet — Ragnes uses built-in keyword defaults (1:1, sprint, standup, review, focus, etc.).</p>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="meeting-rule-list">
            {rules.map((r) => (
              <span key={r.id}
                    data-testid={`meeting-rule-${r.keyword}`}
                    className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border border-border/60 bg-secondary/20">
                <span className="font-mono">"{r.keyword}"</span>
                <span className="text-muted-foreground">→</span>
                <span className={`px-1.5 py-0.5 rounded font-mono text-[10px] border ${KIND_COLORS[r.kind] || ""}`}>{r.kind}</span>
                {r.template && (
                  <span className="text-[9px] text-muted-foreground uppercase tracking-wider">from {r.template}</span>
                )}
                {isAdmin && (
                  <button onClick={() => remove(r.keyword)}
                          className="text-muted-foreground hover:text-destructive ml-1"
                          data-testid={`meeting-rule-delete-${r.keyword}`}>
                    <XIcon className="w-3 h-3" />
                  </button>
                )}
              </span>
            ))}
          </div>
        )}
      </div>
      <Dialog open={showSave} onOpenChange={setShowSave}>
        <DialogContent data-testid="save-template-dialog">
          <DialogHeader>
            <DialogTitle>Save rules as a template</DialogTitle>
            <DialogDescription>
              Snapshots the current {rules.length} rule{rules.length === 1 ? "" : "s"}. Private drafts are only visible to you.
              Publishing makes the template appear for every admin so they can one-click apply it in any workspace they run.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Template name</label>
              <input
                data-testid="save-template-name"
                value={tplName}
                onChange={(e) => setTplName(e.target.value)}
                placeholder="e.g. Growth team standard"
                className="w-full mt-1 px-3 py-2 rounded border border-border/60 bg-background text-sm"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                Category
                {tplSuggestion?.suggested && tplSuggestion.suggested !== "general" && tplSuggestion.confidence > 0 && (
                  <button type="button"
                          onClick={() => setTplCategory(tplSuggestion.suggested)}
                          className="text-[10px] px-2 py-0.5 rounded-full bg-sky-500/15 text-sky-500 hover:bg-sky-500/25 transition-colors normal-case tracking-normal"
                          data-testid="save-template-suggest">
                    <Sparkles className="w-2.5 h-2.5 inline mr-1" />
                    Suggested: {tplSuggestion.suggested}
                  </button>
                )}
              </label>
              <select
                data-testid="save-template-category"
                value={tplCategory}
                onChange={(e) => setTplCategory(e.target.value)}
                className="w-full mt-1 px-3 py-2 rounded border border-border/60 bg-background text-sm"
              >
                <option value="">(auto — general)</option>
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox"
                     data-testid="save-template-publish"
                     checked={tplPublish}
                     onChange={(e) => setTplPublish(e.target.checked)} />
              Publish immediately (share with all admins)
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setShowSave(false)}>Cancel</Button>
              <Button data-testid="save-template-confirm" onClick={saveTemplate} disabled={busy}>
                {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Save template
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}


function DigestSchedulePanel() {
  const { workspace, isAdmin, refresh } = useAuth();
  const [dow, setDow] = useState(workspace?.decay_digest_day_of_week || "fri");
  const [hour, setHour] = useState(workspace?.decay_digest_hour_utc ?? 9);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setDow(workspace?.decay_digest_day_of_week || "fri");
    setHour(workspace?.decay_digest_hour_utc ?? 9);
  }, [workspace?.decay_digest_day_of_week, workspace?.decay_digest_hour_utc]);
  const DOW = [
    { id: "mon", label: "Mon" }, { id: "tue", label: "Tue" },
    { id: "wed", label: "Wed" }, { id: "thu", label: "Thu" },
    { id: "fri", label: "Fri" }, { id: "sat", label: "Sat" }, { id: "sun", label: "Sun" },
  ];
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/workspaces/${workspace.id}/settings`, {
        decay_digest_day_of_week: dow, decay_digest_hour_utc: parseInt(hour),
      });
      await refresh();
      toast.success(`Digest scheduled — ${dow} ${String(hour).padStart(2, "0")}:00 UTC`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  if (!isAdmin) return null;
  return (
    <section className="mb-10" data-testid="digest-schedule-section">
      <h2 className="font-medium mb-1">Weekly decay digest schedule</h2>
      <p className="text-xs text-muted-foreground mb-4 max-w-2xl">
        Pick the day + hour (UTC) the digest goes out. It'll email each owner a mini-list of their aging entries.
      </p>
      <div className="card-flat p-5 flex flex-wrap items-end gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Day</label>
          <div className="flex gap-1 mt-1" data-testid="digest-schedule-days">
            {DOW.map((d) => (
              <button
                key={d.id}
                data-testid={`digest-schedule-day-${d.id}`}
                onClick={() => setDow(d.id)}
                className={`px-2.5 py-1.5 rounded text-xs font-medium border transition-colors ${
                  dow === d.id
                    ? "border-sky-500 bg-sky-500/15 text-sky-500"
                    : "border-border/60 hover:bg-secondary/40"
                }`}
              >{d.label}</button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Hour (UTC)</label>
          <select
            data-testid="digest-schedule-hour"
            value={hour}
            onChange={(e) => setHour(parseInt(e.target.value))}
            className="mt-1 px-3 py-2 rounded border border-border/60 bg-background text-sm"
          >
            {Array.from({ length: 24 }).map((_, h) => (
              <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>
            ))}
          </select>
        </div>
        <Button size="sm" onClick={save} disabled={busy} className="rounded-full"
                data-testid="digest-schedule-save">
          {busy && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
          Save schedule
        </Button>
        <p className="text-[11px] text-muted-foreground w-full">
          Currently: <strong>{dow} {String(hour).padStart(2, "0")}:00 UTC</strong>. Defaults to Friday 09:00 UTC if unset.
        </p>
      </div>
    </section>
  );
}

function TeamInsightDigestPanel() {
  const { workspace, isAdmin, refresh } = useAuth();
  const [dow, setDow] = useState(workspace?.team_insight_digest_day_of_week || "mon");
  const [hour, setHour] = useState(workspace?.team_insight_digest_hour_utc ?? 9);
  const [enabled, setEnabled] = useState(workspace?.team_insight_digest_enabled !== false);
  const [nudgeMailEnabled, setNudgeMailEnabled] = useState(workspace?.weekly_nudge_email_enabled !== false);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendingNudges, setSendingNudges] = useState(false);
  useEffect(() => {
    setDow(workspace?.team_insight_digest_day_of_week || "mon");
    setHour(workspace?.team_insight_digest_hour_utc ?? 9);
    setEnabled(workspace?.team_insight_digest_enabled !== false);
    setNudgeMailEnabled(workspace?.weekly_nudge_email_enabled !== false);
  }, [workspace?.team_insight_digest_day_of_week, workspace?.team_insight_digest_hour_utc, workspace?.team_insight_digest_enabled, workspace?.weekly_nudge_email_enabled]);
  const DOW = [
    { id: "mon", label: "Mon" }, { id: "tue", label: "Tue" },
    { id: "wed", label: "Wed" }, { id: "thu", label: "Thu" },
    { id: "fri", label: "Fri" }, { id: "sat", label: "Sat" }, { id: "sun", label: "Sun" },
  ];
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/workspaces/${workspace.id}/settings`, {
        team_insight_digest_day_of_week: dow,
        team_insight_digest_hour_utc: parseInt(hour),
        team_insight_digest_enabled: enabled,
        weekly_nudge_email_enabled: nudgeMailEnabled,
      });
      await refresh();
      toast.success("Weekly digest settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const sendNow = async () => {
    setSending(true);
    try {
      const r = await api.post("/bot-access/analytics/workspace/digest/send-now");
      if (r.data.skipped === "no_queries") {
        toast.info("No queries this week — nothing to send yet");
      } else {
        toast.success(`Digest sent to ${r.data.emails_sent} admin${r.data.emails_sent === 1 ? "" : "s"}`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSending(false); }
  };
  const sendNudgeMailNow = async () => {
    setSendingNudges(true);
    try {
      const r = await api.post("/bot-access/nudges/weekly-email/send-now");
      toast.success(`Nudge email fired — ${r.data.emails_sent} message${r.data.emails_sent === 1 ? "" : "s"} delivered`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSendingNudges(false); }
  };
  if (!isAdmin) return null;
  return (
    <section className="mb-10" data-testid="team-insight-digest-section">
      <h2 className="font-medium mb-1">Weekly team-insight digest</h2>
      <p className="text-xs text-muted-foreground mb-4 max-w-2xl">
        Emails every admin a recap of when the team asked Ragnes this week — top bots, topics, and auto-detected patterns (weekends, after-hours, peaks).
      </p>
      <div className="card-flat p-5 space-y-3">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox"
                 data-testid="team-insight-enabled"
                 checked={enabled}
                 onChange={(e) => setEnabled(e.target.checked)} />
          Send weekly team-insight digest
        </label>
        <div className={`flex flex-wrap items-end gap-3 ${enabled ? "" : "opacity-40 pointer-events-none"}`}>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Day</label>
            <div className="flex gap-1 mt-1" data-testid="team-insight-days">
              {DOW.map((d) => (
                <button
                  key={d.id}
                  data-testid={`team-insight-day-${d.id}`}
                  onClick={() => setDow(d.id)}
                  className={`px-2.5 py-1.5 rounded text-xs font-medium border transition-colors ${
                    dow === d.id
                      ? "border-fuchsia-500 bg-fuchsia-500/15 text-fuchsia-500"
                      : "border-border/60 hover:bg-secondary/40"
                  }`}
                >{d.label}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Hour (UTC)</label>
            <select
              data-testid="team-insight-hour"
              value={hour}
              onChange={(e) => setHour(parseInt(e.target.value))}
              className="mt-1 px-3 py-2 rounded border border-border/60 bg-background text-sm"
            >
              {Array.from({ length: 24 }).map((_, h) => (
                <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 pt-1">
          <Button size="sm" onClick={save} disabled={busy} className="rounded-full"
                  data-testid="team-insight-save">
            {busy && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
            Save schedule
          </Button>
          <Button size="sm" variant="outline" onClick={sendNow} disabled={sending || !enabled}
                  className="rounded-full"
                  data-testid="team-insight-send-now">
            {sending && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
            Send preview now
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Currently: <strong>{enabled ? `${dow} ${String(hour).padStart(2, "0")}:00 UTC` : "OFF"}</strong>. Defaults to Monday 09:00 UTC.
        </p>
        <div className="pt-4 mt-4 border-t border-border/40 space-y-2" data-testid="weekly-nudge-email-block">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox"
                   data-testid="weekly-nudge-email-enabled"
                   checked={nudgeMailEnabled}
                   onChange={(e) => setNudgeMailEnabled(e.target.checked)} />
            Send Monday nudge roundup email
          </label>
          <p className="text-[11px] text-muted-foreground">
            Every Monday 09:13 UTC, Ragnes emails each teammate their unaddressed heatmap nudges (Friday recap, weekend load, after-hours). Nudges you dismiss or complete in-app are automatically excluded.
          </p>
          <Button size="sm" variant="outline" onClick={sendNudgeMailNow}
                  disabled={sendingNudges || !nudgeMailEnabled}
                  className="rounded-full"
                  data-testid="weekly-nudge-email-send-now">
            {sendingNudges && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
            Send nudge email now
          </Button>
        </div>
      </div>
    </section>
  );
}



function CustomNudgeRulesPanel() {
  const { isAdmin } = useAuth();
  const [rules, setRules] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    name: "", keyword: "", threshold: 5, window_days: 7,
    body: "", cta_label: "Open Knowledge", cta_href: "/app/knowledge", tone: "info",
  });
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  useEffect(() => {
    if (!showAdd) { setPreview(null); return; }
    if (!form.keyword || form.keyword.trim().length < 2) { setPreview(null); return; }
    const handle = setTimeout(async () => {
      setPreviewing(true);
      try {
        const r = await api.post("/bot-access/nudge-rules/preview", {
          keyword: form.keyword.trim(), threshold: form.threshold,
          window_days: form.window_days,
        });
        setPreview(r.data);
      } catch { setPreview(null); }
      finally { setPreviewing(false); }
    }, 400);
    return () => clearTimeout(handle);
  }, [form.keyword, form.threshold, form.window_days, showAdd]);
  const load = async () => {
    try {
      const r = await api.get("/bot-access/nudge-rules");
      setRules(r.data.rules || []);
    } catch { setRules([]); }
  };
  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);
  const submit = async () => {
    if (form.name.trim().length < 2 || form.keyword.trim().length < 2 || form.body.trim().length < 4) {
      return toast.error("Name, keyword and message are all required");
    }
    setBusy(true);
    try {
      await api.post("/bot-access/nudge-rules", form);
      toast.success("Custom nudge rule added");
      setShowAdd(false);
      setForm({ name: "", keyword: "", threshold: 5, window_days: 7,
                body: "", cta_label: "Open Knowledge",
                cta_href: "/app/knowledge", tone: "info" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const toggle = async (r) => {
    try {
      await api.patch(`/bot-access/nudge-rules/${r.id}`, { enabled: !r.enabled });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.name}"?`)) return;
    try {
      await api.delete(`/bot-access/nudge-rules/${r.id}`);
      toast.success("Rule deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const publish = async (r) => {
    try {
      await api.post(`/bot-access/nudge-rules/${r.id}/publish`);
      toast.success("Published — visible in every admin's Rule Library");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const unpublish = async (r) => {
    try {
      await api.post(`/bot-access/nudge-rules/${r.id}/unpublish`);
      toast.success("Made private");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  if (!isAdmin) return null;
  return (
    <section className="mb-10" data-testid="custom-nudge-rules-section">
      <div className="flex items-center justify-between mb-1">
        <h2 className="font-medium">Custom nudge rules</h2>
        <div className="flex items-center gap-2">
          <Link to="/app/nudge-library" className="text-xs text-sky-500 hover:underline"
                data-testid="nudge-library-link">
            Browse library →
          </Link>
          <Button size="sm" variant="outline" className="rounded-full"
                  onClick={() => setShowAdd(true)}
                  data-testid="custom-nudge-add-btn">
            <Sparkles className="w-3 h-3 mr-1.5" /> New rule
          </Button>
        </div>
      </div>
      <p className="text-xs text-muted-foreground mb-4 max-w-2xl">
        Fire your own nudges when chat activity crosses a threshold you care about. Example: "if &gt;10 client demo queries this week, remind me to refresh case studies."
      </p>
      <div className="card-flat p-5">
        {rules.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">No custom rules yet — add your first one to get personalised weekly nudges.</p>
        ) : (
          <div className="space-y-2" data-testid="custom-nudge-rules-list">
            {rules.map((r) => (
              <div key={r.id}
                   data-testid={`custom-nudge-rule-${r.id}`}
                   className={`flex items-start gap-3 p-3 rounded border ${r.enabled ? "border-border/60 bg-secondary/20" : "border-border/40 bg-secondary/5 opacity-60"}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium flex items-center gap-2 flex-wrap">
                    {r.name}
                    <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ${r.tone === "warn" ? "bg-amber-500/15 text-amber-500" : "bg-sky-500/15 text-sky-500"}`}>{r.tone}</span>
                    {r.is_published && (
                      <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-500">published</span>
                    )}
                    {r.installed_from && (
                      <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-500">installed</span>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Fires when <span className="font-mono">"{r.keyword}"</span> appears in ≥{r.threshold} chat quer{r.threshold === 1 ? "y" : "ies"} over {r.window_days}d
                  </p>
                  <p className="text-xs mt-1 text-foreground/80 line-clamp-2">{r.body}</p>
                </div>
                <div className="flex flex-col items-end gap-1.5 shrink-0">
                  <label className="flex items-center gap-1 text-xs">
                    <input type="checkbox"
                           data-testid={`custom-nudge-toggle-${r.id}`}
                           checked={r.enabled}
                           onChange={() => toggle(r)} />
                    <span>{r.enabled ? "on" : "off"}</span>
                  </label>
                  {!r.installed_from && (
                    r.is_published ? (
                      <button onClick={() => unpublish(r)}
                              data-testid={`custom-nudge-unpublish-${r.id}`}
                              className="text-[10px] uppercase tracking-wider text-amber-500 hover:underline">
                        Unpublish
                      </button>
                    ) : (
                      <button onClick={() => publish(r)}
                              data-testid={`custom-nudge-publish-${r.id}`}
                              className="text-[10px] uppercase tracking-wider text-emerald-500 hover:underline">
                        Publish
                      </button>
                    )
                  )}
                  <button onClick={() => remove(r)}
                          data-testid={`custom-nudge-delete-${r.id}`}
                          className="text-muted-foreground hover:text-destructive">
                    <XIcon className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent data-testid="custom-nudge-add-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>New custom nudge rule</DialogTitle>
            <DialogDescription>
              We'll email you (and show an in-app nudge) when this pattern shows up. Nudges you dismiss or action are excluded automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Rule name</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     data-testid="custom-nudge-name"
                     placeholder="e.g. Client demo watch"
                     className="w-full mt-1 px-3 py-2 rounded border border-border/60 bg-background text-sm" />
            </div>
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Keyword</label>
                <input value={form.keyword} onChange={(e) => setForm({ ...form, keyword: e.target.value })}
                       data-testid="custom-nudge-keyword"
                       placeholder="e.g. client demo"
                       className="w-full mt-1 px-3 py-2 rounded border border-border/60 bg-background text-sm" />
              </div>
              <div className="w-24">
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Threshold</label>
                <input type="number" min={1} max={1000}
                       data-testid="custom-nudge-threshold"
                       value={form.threshold}
                       onChange={(e) => setForm({ ...form, threshold: parseInt(e.target.value) || 1 })}
                       className="w-full mt-1 px-3 py-2 rounded border border-border/60 bg-background text-sm" />
              </div>
              <div className="w-24">
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Window (d)</label>
                <input type="number" min={1} max={90}
                       data-testid="custom-nudge-window"
                       value={form.window_days}
                       onChange={(e) => setForm({ ...form, window_days: parseInt(e.target.value) || 7 })}
                       className="w-full mt-1 px-3 py-2 rounded border border-border/60 bg-background text-sm" />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Nudge message</label>
              <textarea rows={2} value={form.body}
                        data-testid="custom-nudge-body"
                        onChange={(e) => setForm({ ...form, body: e.target.value })}
                        placeholder="e.g. You've had a lot of client-demo chatter — refresh the case studies."
                        className="w-full mt-1 px-3 py-2 rounded border border-border/60 bg-background text-sm" />
            </div>
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground">CTA label</label>
                <input value={form.cta_label}
                       data-testid="custom-nudge-cta-label"
                       onChange={(e) => setForm({ ...form, cta_label: e.target.value })}
                       className="w-full mt-1 px-3 py-2 rounded border border-border/60 bg-background text-sm" />
              </div>
              <div className="flex-1">
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground">CTA href</label>
                <input value={form.cta_href}
                       data-testid="custom-nudge-cta-href"
                       onChange={(e) => setForm({ ...form, cta_href: e.target.value })}
                       className="w-full mt-1 px-3 py-2 rounded border border-border/60 bg-background text-sm" />
              </div>
              <div className="w-24">
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Tone</label>
                <select value={form.tone}
                        data-testid="custom-nudge-tone"
                        onChange={(e) => setForm({ ...form, tone: e.target.value })}
                        className="w-full mt-1 px-3 py-2 rounded border border-border/60 bg-background text-sm">
                  <option value="info">info</option>
                  <option value="warn">warn</option>
                </select>
              </div>
            </div>
            {form.keyword.trim().length >= 2 && (
              <div className={`rounded-lg border p-3 text-xs ${
                preview?.would_fire
                  ? "border-emerald-500/40 bg-emerald-500/5"
                  : "border-border/60 bg-secondary/20"
              }`} data-testid="custom-nudge-preview">
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles className={`w-3.5 h-3.5 ${preview?.would_fire ? "text-emerald-500" : "text-muted-foreground"}`} />
                  <span className="font-medium text-sm">Live preview</span>
                  {previewing && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
                </div>
                {preview ? (
                  <>
                    <p className={preview.would_fire ? "text-emerald-500" : "text-muted-foreground"}>
                      <strong data-testid="custom-nudge-preview-count">{preview.match_count}</strong>
                      {" "}matching quer{preview.match_count === 1 ? "y" : "ies"} for
                      <span className="font-mono"> "{form.keyword}"</span> in the last {preview.window_days}d
                      · threshold {preview.threshold}
                      <span data-testid="custom-nudge-preview-would-fire" className={`ml-2 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider ${preview.would_fire ? "bg-emerald-500/20 text-emerald-500" : "bg-zinc-500/20 text-zinc-500"}`}>
                        {preview.would_fire ? "will fire" : "won't fire yet"}
                      </span>
                    </p>
                    {preview.sample_questions.length > 0 && (
                      <ul className="mt-2 space-y-1 text-muted-foreground list-disc list-inside">
                        {preview.sample_questions.slice(0, 3).map((q, i) => (
                          <li key={i} className="truncate">"{q}"</li>
                        ))}
                      </ul>
                    )}
                  </>
                ) : (
                  <p className="text-muted-foreground">Type at least 2 characters to see if this rule would fire on this week's chat.</p>
                )}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setShowAdd(false)}>Cancel</Button>
              <Button onClick={submit} disabled={busy}
                      data-testid="custom-nudge-submit">
                {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Save rule
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}




export default function Settings() {
  const { workspace, isAdmin } = useAuth();
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/integrations");
      setIntegrations(data);
    } catch { setIntegrations([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [workspace?.id]);

  const connected = new Map(integrations.map((i) => [i.provider, i]));

  const connect = async (providerId) => {
    try {
      const { data } = await api.post(`/integrations/${providerId}/authorize`);
      if (data.mocked) {
        toast.success(`${providerId} connected (mock)`);
        load();
        return;
      }
      if (data.authorize_url) {
        window.location.href = data.authorize_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start OAuth");
    }
  };

  const disconnect = async (providerId) => {
    if (!window.confirm(`Disconnect ${providerId}?`)) return;
    try {
      await api.delete(`/integrations/${providerId}`);
      toast.success("Disconnected");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to disconnect");
    }
  };

  return (
    <div className="p-8 max-w-4xl" data-testid="settings-page">
      <header className="mb-8">
        <p className="eyebrow mb-3">Settings</p>
        <h1 className="font-display text-3xl">Integrations</h1>
        <p className="mt-2 text-muted-foreground">
          Connect the tools your team already uses. Only Admins can change these.
        </p>
      </header>

      <PrivacySection />
      <ComplianceSection />

      <section className="mb-10" data-testid="personal-integrations-section">
        <h2 className="font-medium mb-3">Personal integrations</h2>
        <p className="text-xs text-muted-foreground mb-4">
          These connect to <em>your</em> account (not the workspace) — each teammate links their own Google Calendar / WhatsApp.
        </p>
        <div className="grid gap-4 md:grid-cols-2">
          <GCalPanel />
          <WhatsAppPanel />
        </div>
      </section>

      <MeetingTypeRulesPanel />
      <DigestSchedulePanel />
      <TeamInsightDigestPanel />
      <CustomNudgeRulesPanel />

      <h2 className="font-medium mb-3">Workspace integrations</h2>

      <div className="grid gap-4 md:grid-cols-2">
        {PROVIDERS.map((p) => {
          const active = connected.get(p.id);
          const Icon = p.icon;
          return (
            <div key={p.id} className="card-flat p-5 flex flex-col gap-4" data-testid={`integration-card-${p.id}`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-secondary/60 flex items-center justify-center">
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium">{p.name}</p>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                    {active
                      ? <><CheckCircle2 className="w-3 h-3 text-emerald-500" /> Connected</>
                      : <><XCircle className="w-3 h-3 text-muted-foreground" /> Not connected</>}
                  </p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed flex-1">
                {p.description}
              </p>
              {active ? (
                <div className="flex items-center gap-2">
                  <div className="text-[11px] text-muted-foreground flex-1 truncate">
                    {active.metadata?.account_name || active.metadata?.team_name || "OAuth session active"}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => disconnect(p.id)}
                    disabled={!isAdmin}
                    className="rounded-full text-destructive hover:text-destructive"
                    data-testid={`integration-disconnect-${p.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Disconnect
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={() => connect(p.id)}
                  disabled={!isAdmin}
                  className="rounded-full"
                  data-testid={`integration-connect-${p.id}`}
                >
                  <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Connect with OAuth
                </Button>
              )}
            </div>
          );
        })}
      </div>

      <div className="card-flat mt-8 p-5 border-dashed">
        <div className="flex items-start gap-3">
          <Sparkles className="w-5 h-5 mt-0.5 text-muted-foreground shrink-0" />
          <div>
            <p className="font-medium">Assistant with 300+ integrations</p>
            <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
              Once connected, the AI assistant can search across your tools and answer with sourced citations.
              Additional integrations (Google Drive, Notion, Salesforce…) unlock automatically as you connect them here.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
